import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Orders.css';
import Sidebar from '../../components/layout/Sidebar/Sidebar';
import apiClient from '../../services/api';
import {
    ChevronDown, Calendar, SlidersHorizontal,
    Download, MoreVertical,
    ChevronLeft, ChevronRight,
    TrendingUp, TrendingDown, Clock, Zap,
    ShoppingBag, CalendarClock, XCircle, Activity,
    X, User, MapPin, CreditCard, Info
} from 'lucide-react';

const Orders = () => {
    const entity = JSON.parse(localStorage.getItem('entity') || '{}');
    const isCarRental = entity.entityType === 'car';
    const [allBookings, setAllBookings] = useState([]);
    const [filteredBookings, setFilteredBookings] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('All');
    const [dateFilter, setDateFilter] = useState('All Time');
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [isCanceling, setIsCanceling] = useState(false);
    const [zoomImage, setZoomImage] = useState(null);
    const navigate = useNavigate();

    const fetchBookings = async () => {
        try {
            const res = await apiClient.get('/bookings');
            if (res.data && res.data.data) {
                const fetched = res.data.data.map(b => {
                    const checkInDate = new Date(b.check_in).toLocaleDateString();
                    const checkOutDate = new Date(b.check_out).toLocaleDateString();
                        return {
                        id: `#BK-${b.id}`,
                        rawId: b.id,
                        unit: b.title || b.entity_type, 
                        checkIn: checkInDate, 
                        checkInRaw: b.check_in,
                        checkOut: checkOutDate, 
                        totalAmount: b.total_price,
                        amountPaid: b.amount_paid,
                        balance: b.total_price - b.amount_paid,
                        plan: b.payment_plan,
                        duration: b.entity_type === 'Car' ? 'Rental' : 'Stay', 
                        status: b.status || 'Pending',
                        createdAt: b.created_at,
                        guestName: b.guest_name || 'Guest User',
                        guestEmail: b.guest_email || 'guest@somstay.com',
                        guestPhone: b.guest_phone || 'N/A',
                        entityType: b.entity_type,
                        roomType: b.room_type || null,
                        roomNumber: b.room_number || null,
                        verificationCode: b.verification_code || null,
                        addOns: (typeof b.extra_services === 'string' ? JSON.parse(b.extra_services || '[]') : b.extra_services) || [],
                        licensePhoto: b.license_photo || null,
                        passportPhoto: b.passport_photo || null,
                        selfiePhoto: b.selfie_photo || null,
                        licenseNumber: b.license_number || null,
                        pickupLocation: b.pickup_location || null,
                        dropoffLocation: b.dropoff_location || null,
                        deliveryType: b.delivery_type || null,
                    };
                });
                setAllBookings(fetched);
                setFilteredBookings(fetched);
            }
        } catch (err) {
            console.error("Failed to load ordered bookings:", err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchBookings();
    }, []);

    const handleCancelBooking = async (bookingId) => {
        if (!window.confirm(`Are you sure you want to cancel booking #${bookingId}?`)) return;
        setIsCanceling(true);
        try {
            await apiClient.put(`/bookings/${bookingId}/status`, { status: 'Cancelled' });
            alert('Booking cancelled successfully.');
            setSelectedBooking(null);
            fetchBookings();
        } catch (error) {
            console.error('Error cancelling booking:', error);
            alert('Failed to cancel booking.');
        } finally {
            setIsCanceling(false);
        }
    };

    useEffect(() => {
        let updated = [...allBookings];

        // Status Filter
        if (statusFilter !== 'All') {
            updated = updated.filter(b => b.status.toLowerCase() === statusFilter.toLowerCase());
        }

        // Date Filter
        const now = new Date();
        if (dateFilter === 'This Week') {
            const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
            updated = updated.filter(b => new Date(b.createdAt) >= startOfWeek);
        } else if (dateFilter === 'Today') {
            const todayStr = now.toDateString();
            updated = updated.filter(b => new Date(b.createdAt).toDateString() === todayStr);
        }

        setFilteredBookings(updated);
    }, [statusFilter, dateFilter, allBookings]);

    const downloadReport = () => {
        if (filteredBookings.length === 0) return alert('No data to download.');

        const headers = ["Booking ID", "Unit", "Guest Name", "Check-In", "Status", "Total Amount", "Paid", "Balance"];
        const rows = filteredBookings.map(b => [
            b.id, b.unit, b.guestName, b.checkIn, b.status, b.totalAmount, b.amountPaid, b.balance
        ]);

        let csvContent = "data:text/csv;charset=utf-8," 
            + headers.join(",") + "\n"
            + rows.map(e => e.join(",")).join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `Somstay_Report_${new Date().toLocaleDateString()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const stats = [
        {
            label: "Total Orders",
            value: allBookings.length,
            trend: 'All time',
            trendType: 'up',
            icon: <ShoppingBag size={20} />,
            color: 'blue',
        },
        {
            label: 'Confirmed',
            value: allBookings.filter(b => b.status === 'Confirmed').length,
            trend: 'Active',
            trendType: 'neutral',
            icon: <CalendarClock size={20} />,
            color: 'teal',
        },
        {
            label: 'Completed',
            value: allBookings.filter(b => b.status === 'Completed').length,
            trend: 'Finished',
            trendType: 'up',
            icon: <Activity size={20} />,
            color: 'green',
        },
        {
            label: 'Cancelled',
            value: allBookings.filter(b => b.status === 'Cancelled').length,
            trend: 'Lost',
            trendType: 'down',
            icon: <XCircle size={20} />,
            color: 'red',
        },
        {
            label: 'Pending',
            value: allBookings.filter(b => b.status === 'Pending').length,
            trend: 'Needs action',
            trendType: 'active',
            icon: <Zap size={20} />,
            color: 'blue',
        },
    ];

    const TrendIcon = ({ type }) => {
        if (type === 'up')      return <TrendingUp size={13} />;
        if (type === 'down')    return <TrendingDown size={13} />;
        if (type === 'neutral') return <Clock size={13} />;
        if (type === 'active')  return <Zap size={13} />;
        return null;
    };

    return (
        <div className="orders-page-container">
            <Sidebar />
            <main className="main-content">

                {/* Page Title + Filters */}
                <div className="orders-top-row">
                    <div className="orders-title-block">
                        <h1>{isCarRental ? 'Car Reservations' : 'Orders'}</h1>
                        <p>{isCarRental ? 'Track and manage your vehicle rentals and reservations' : 'Manage and track your bookings and inventory status'}</p>
                    </div>
                    <div className="orders-filters">
                        <div className="filter-group">
                            <span className="filter-label">STATUS</span>
                            <select 
                                className="filter-select"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="All">All Statuses</option>
                                <option value="Confirmed">Confirmed</option>
                                <option value="Pending">Pending</option>
                                <option value="Cancelled">Cancelled</option>
                                <option value="Completed">Completed</option>
                            </select>
                        </div>
                        <div className="filter-group">
                            <span className="filter-label">DATE RANGE</span>
                            <select 
                                className="filter-select"
                                value={dateFilter}
                                onChange={(e) => setDateFilter(e.target.value)}
                            >
                                <option value="All Time">All Time</option>
                                <option value="Today">Today</option>
                                <option value="This Week">This Week</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Stat Cards */}
                <div className="orders-stats">
                    {stats.map((s, i) => (
                        <div key={i} className={`orders-stat-card accent-${s.color}`}>
                            <div className="stat-card-top">
                                <div className={`stat-icon-box icon-${s.color}`}>
                                    {s.icon}
                                </div>
                                <div>
                                    <p className="stat-card-label">{s.label}</p>
                                    <p className="stat-card-value">{s.value}</p>
                                </div>
                            </div>
                            <div className={`stat-card-trend trend-${s.trendType}`}>
                                <TrendIcon type={s.trendType} />
                                <span>{s.trend}</span>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Recent Bookings Table */}
                <div className="table-section">
                    <div className="table-top-row">
                        <h2>Recent Bookings</h2>
                        <div className="table-actions">
                            <Download 
                                size={17} 
                                className="tbl-action-icon" 
                                onClick={downloadReport}
                                title="Download CSV Report"
                            />
                            <MoreVertical size={17} className="tbl-action-icon" />
                        </div>
                    </div>

                    <div className="table-wrapper">
                        <table className="bookings-table">
                            <thead>
                                <tr>
                                    <th>BOOKING ID</th>
                                    <th>UNIT</th>
                                    <th>CHECK-IN / PICKUP</th>
                                    <th>DURATION</th>
                                    <th>TOTAL</th>
                                    <th>PAID</th>
                                    <th>BALANCE</th>
                                    <th>STATUS</th>
                                    <th className="col-actions">ACTIONS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredBookings.length > 0 ? (
                                    filteredBookings.map((b, i) => (
                                        <tr key={i}>
                                            <td>
                                                <div className="booking-id-cell">
                                                    <span className="booking-id-bar" />
                                                    <span className="booking-id-text">{b.id}</span>
                                                </div>
                                            </td>
                                            <td className="unit-cell">{b.unit}</td>
                                            <td>{b.checkIn}</td>
                                            <td>{b.duration}</td>
                                            <td style={{ fontWeight: '700' }}>${b.totalAmount}</td>
                                            <td style={{ color: '#059669', fontWeight: '700' }}>${b.amountPaid}</td>
                                            <td style={{ color: b.balance > 0 ? '#DC2626' : '#6B7280', fontWeight: '700' }}>
                                                {b.balance > 0 ? `$${b.balance.toFixed(2)}` : 'Full'}
                                            </td>
                                            <td>
                                                <span className={`order-status-pill pill-${b.status.toLowerCase()}`}>
                                                    {b.status}
                                                </span>
                                            </td>
                                            <td className="col-actions">
                                                <button className="view-details-btn" onClick={() => setSelectedBooking(b)}>
                                                    View Details
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="9" style={{ textAlign: 'center', padding: '2rem' }}>No bookings found matching filters.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="table-footer">
                        <p className="entries-text">SHOWING {filteredBookings.length} ENTRIES</p>
                        <div className="pagination">
                            <button className="page-nav-btn"><ChevronLeft size={14} /></button>
                            <button className="page-num-btn active">1</button>
                            <button className="page-nav-btn"><ChevronRight size={14} /></button>
                        </div>
                    </div>
                </div>
            </main>

            {/* Booking Details Modal */}
            {selectedBooking && (
                <div className="modal-overlay" onClick={() => setSelectedBooking(null)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Booking Details {selectedBooking.id}</h2>
                            <button className="close-modal-btn" onClick={() => setSelectedBooking(null)}><X size={18} /></button>
                        </div>
                        <div className="modal-body">
                            <div className="detail-section">
                                <h4 className="section-title"><User size={14} /> Guest Information</h4>
                                <div className="details-grid">
                                    <div className="detail-item">
                                        <label>Full Name</label>
                                        <span>{selectedBooking.guestName}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Contact Phone</label>
                                        <span>{selectedBooking.guestPhone}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="detail-section">
                                <h4 className="section-title"><MapPin size={14} /> Reservation Info</h4>
                                <div className="details-grid">
                                    <div className="detail-item">
                                        <label>Reserved Unit</label>
                                        <span>{selectedBooking.unit}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Booking Type</label>
                                        <span>{selectedBooking.entityType}</span>
                                    </div>
                                    {selectedBooking.roomType && (
                                        <div className="detail-item">
                                            <label>Room Type</label>
                                            <span style={{ fontWeight: '700', color: '#6366f1' }}>
                                                {selectedBooking.roomType}{selectedBooking.roomNumber ? ` (Room ${selectedBooking.roomNumber})` : ''}
                                            </span>
                                        </div>
                                    )}
                                    <div className="detail-item">
                                        <label>Check-In Date</label>
                                        <span>{selectedBooking.checkIn}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Check-Out Date</label>
                                        <span>{selectedBooking.checkOut}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Driver Documents Section for Car Rentals */}
                            {(selectedBooking.entityType === 'Car' || selectedBooking.licensePhoto || selectedBooking.passportPhoto || selectedBooking.selfiePhoto) && (
                                <div className="detail-section" style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                    <h4 className="section-title" style={{ color: '#0288AC', marginBottom: '8px' }}>
                                        🪪 Renter Documents & Identity Photos
                                    </h4>
                                    {selectedBooking.licenseNumber && (
                                        <p style={{ fontSize: '13px', color: '#475569', marginBottom: '12px' }}>
                                            <strong>License Number:</strong> {selectedBooking.licenseNumber}
                                        </p>
                                    )}
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px' }}>
                                        <div style={{ textAlign: 'center' }}>
                                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', display: 'block', marginBottom: '4px' }}>
                                                License (Front)
                                            </label>
                                            {selectedBooking.licensePhoto ? (
                                                <div 
                                                    style={{ cursor: 'pointer', borderRadius: '8px', overflow: 'hidden', border: '1.5px solid #cbd5e1', height: '100px', background: '#fff' }}
                                                    onClick={() => setZoomImage(selectedBooking.licensePhoto)}
                                                    title="Click to zoom image"
                                                >
                                                    <img src={selectedBooking.licensePhoto} alt="License Front" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                </div>
                                            ) : (
                                                <div style={{ height: '100px', background: '#f1f5f9', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#94a3b8' }}>
                                                    Not Provided
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ textAlign: 'center' }}>
                                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', display: 'block', marginBottom: '4px' }}>
                                                Passport / ID (Back)
                                            </label>
                                            {selectedBooking.passportPhoto ? (
                                                <div 
                                                    style={{ cursor: 'pointer', borderRadius: '8px', overflow: 'hidden', border: '1.5px solid #cbd5e1', height: '100px', background: '#fff' }}
                                                    onClick={() => setZoomImage(selectedBooking.passportPhoto)}
                                                    title="Click to zoom image"
                                                >
                                                    <img src={selectedBooking.passportPhoto} alt="Passport" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                </div>
                                            ) : (
                                                <div style={{ height: '100px', background: '#f1f5f9', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#94a3b8' }}>
                                                    Not Provided
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ textAlign: 'center' }}>
                                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', display: 'block', marginBottom: '4px' }}>
                                                Selfie Verification
                                            </label>
                                            {selectedBooking.selfiePhoto ? (
                                                <div 
                                                    style={{ cursor: 'pointer', borderRadius: '8px', overflow: 'hidden', border: '1.5px solid #cbd5e1', height: '100px', background: '#fff' }}
                                                    onClick={() => setZoomImage(selectedBooking.selfiePhoto)}
                                                    title="Click to zoom image"
                                                >
                                                    <img src={selectedBooking.selfiePhoto} alt="Selfie" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                </div>
                                            ) : (
                                                <div style={{ height: '100px', background: '#f1f5f9', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#94a3b8' }}>
                                                    Not Provided
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <p style={{ fontSize: '11px', color: '#0288AC', marginTop: '8px', textAlign: 'center' }}>
                                        🔍 Click any document photo to enlarge
                                    </p>
                                </div>
                            )}

                            <div className="detail-section">
                                <h4 className="section-title"><CreditCard size={14} /> Payment & Plan</h4>
                                <div className="payment-breakdown">
                                    <div className="pay-row">
                                        <span>Payment Plan</span>
                                        <span className="bold">{selectedBooking.plan || 'Standard'}</span>
                                    </div>
                                    {selectedBooking.addOns && selectedBooking.addOns.length > 0 && (
                                        <>
                                            <div style={{ padding: '12px 0', borderBottom: '1px solid #f1f5f9', borderTop: '1px solid #f1f5f9', margin: '8px 0' }}>
                                                <p style={{ fontWeight: '700', fontSize: '13px', color: '#6366f1', marginBottom: '8px' }}>Add-on Services</p>
                                                {selectedBooking.addOns.map((addon, idx) => {
                                                    const addonName = typeof addon === 'string' ? addon : (addon.name || addon.service_name || addon.title || `Service #${addon.service_id || idx + 1}`);
                                                    const addonPrice = typeof addon === 'object' ? (addon.price_at_booking || addon.price || 0) : 0;
                                                    return (
                                                        <div key={idx} className="pay-row" style={{ paddingLeft: '8px', marginBottom: '4px' }}>
                                                            <span style={{ fontSize: '13px' }}>✓ {addonName}{addon.quantity > 1 ? ` x${addon.quantity}` : ''}</span>
                                                            <span className="bold" style={{ fontSize: '13px' }}>
                                                                {addonPrice ? `+$${(parseFloat(addonPrice) * (addon.quantity || 1)).toFixed(2)}` : 'Included'}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    )}
                                    <div className="pay-row">
                                        <span>Total Price</span>
                                        <span>${selectedBooking.totalAmount}</span>
                                    </div>
                                    <div className="pay-row">
                                        <span>Amount Paid</span>
                                        <span style={{color: '#059669'}}>-${selectedBooking.amountPaid}</span>
                                    </div>
                                    <div className="pay-row total">
                                        <span>Remaining Balance</span>
                                        <span>${selectedBooking.balance.toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            {selectedBooking.status !== 'Cancelled' && (
                                <button 
                                    className="btn-danger" 
                                    style={{ backgroundColor: '#dc2626', color: 'white', border: 'none', padding: '10px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}
                                    onClick={() => handleCancelBooking(selectedBooking.rawId || selectedBooking.id)}
                                    disabled={isCanceling}
                                >
                                    {isCanceling ? 'Canceling...' : 'Cancel Booking'}
                                </button>
                            )}
                            <button className="btn-secondary" onClick={() => setSelectedBooking(null)}>Close</button>
                            {selectedBooking.verificationCode && (
                                <button className="btn-primary" style={{ background: 'linear-gradient(135deg, #6366f1, #4f46e5)' }} onClick={() => {
                                    setSelectedBooking(null);
                                    navigate('/verify-booking', { state: { prefill: selectedBooking.verificationCode } });
                                }}>
                                    <Info size={15} style={{ marginRight: 6 }} /> Verify Check-In
                                </button>
                            )}
                            <button className="btn-primary" onClick={() => {
                                window.print();
                            }}>Print Receipt</button>
                        </div>
                    </div>
                </div>
            )}
            {/* Image Zoom Lightbox Modal */}
            {zoomImage && (
                <div 
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 99999,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        padding: '20px'
                    }}
                    onClick={() => setZoomImage(null)}
                >
                    <button 
                        style={{
                            position: 'absolute', top: 20, right: 20,
                            background: '#fff', border: 'none', borderRadius: '50%',
                            width: 36, height: 36, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}
                        onClick={() => setZoomImage(null)}
                    >
                        <X size={20} color="#000" />
                    </button>
                    <img 
                        src={zoomImage} 
                        alt="Zoomed Document" 
                        style={{ maxWidth: '90%', maxHeight: '85%', borderRadius: '12px', boxShadow: '0 8px 30px rgba(0,0,0,0.5)' }} 
                        onClick={e => e.stopPropagation()}
                    />
                </div>
            )}
        </div>
    );
};

export default Orders;
